"use client";

import React, { useState, useEffect } from "react";
import emailjs from "emailjs-com";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  AlertCircle,
  User,
  MessageSquare,
  Leaf,
  Shield,
  Award,
  ArrowRight,
  Sparkles,
  Globe,
  Heart
} from "lucide-react";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSent, setIsSent] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [focusedField, setFocusedField] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };


  const sanitizeInput = (value) => {
    const div = document.createElement('div');
    div.innerText = value;
    return div.innerHTML;
  };
  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
  
    // Nettoyage XSS
    const name = sanitizeInput(formData.name.trim());
    const email = sanitizeInput(formData.email.trim());
    const message = sanitizeInput(formData.message.trim());
  
    // 🔒 Validation locale AVANT envoi
    if (!name || name.length < 2) {
      toast.error("❌ Le nom est requis et doit contenir au moins 2 caractères.");
      setIsSubmitting(false);
      return;
    }
  
    if (!email || !isValidEmail(email)) {
      toast.error("❌ Adresse email invalide.");
      setIsSubmitting(false);
      return;
    }
  
    if (!message || message.length < 4) {
      toast.error("❌ Le message doit contenir au moins 10 caractères.");
      setIsSubmitting(false);
      return;
    }
  
    // ✅ Si tout est bon, on envoie
    try {
      const response = await fetch("https://api.greenecobagsusa.com/api/ContactMessage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          subject: "Contact Form Submission",
          message
        })
      });
  
      const result = await response.json();
  
      if (response.ok) {
        setIsSent(true);
        setFormData({ name: "", email: "", message: "" });
        toast.success("✅ Message envoyé avec succès !");
      } else {
        const errorMessage =
          result?.error ||
          result?.message ||
          result?.detail ||
          "Le serveur n'a pas retourné de message d'erreur.";
        toast.error(`❌ Erreur serveur : ${errorMessage}`);
      }
    } catch (error) {
      toast.error("❌ Erreur réseau : " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  



  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-pulse animation-delay-4000"></div>
      </div>

      <ToastContainer position="bottom-right" autoClose={3000} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Header Section */}
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} text-center mb-16`}>
          <div className="inline-flex items-center space-x-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <MessageSquare className="w-4 h-4" />
            <span>Get in Touch</span>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Contact Us</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
          <div className="w-20 h-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mx-auto mt-6"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Form */}
          <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center">
                  <Send className="mr-3 text-green-600" size={32} />
                  Send us a Message
                </h2>
                <p className="text-gray-600 text-lg">
                  Fill out the form below and we'll get back to you within 24 hours.
                </p>
              </div>

              {isSent ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Message Sent Successfully!</h3>
                  <p className="text-gray-600 mb-6">Thank you for contacting us. We'll get back to you soon.</p>
                  <button
                    onClick={() => setIsSent(false)}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Field */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Full Name</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className={`w-5 h-5 ${focusedField === 'name' ? 'text-green-600' : 'text-gray-400'} transition-colors duration-200`} />
                      </div>
                      <input
                        type="text"
                        name="name"
                        required
                        maxLength={100}
                        pattern="[A-Za-zÀ-ÿ '-]{2,100}"
                        placeholder="Enter your full name"
                        value={formData.name}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('name')}
                        onBlur={() => setFocusedField('')}
                        className={`block w-full pl-10 pr-4 py-4 border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${focusedField === 'name'
                          ? 'border-green-300 bg-green-50'
                          : 'border-gray-300 bg-white hover:border-gray-400'
                          }`}
                      />
                    </div>
                  </div>

                  {/* Email Field */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Email Address</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className={`w-5 h-5 ${focusedField === 'email' ? 'text-green-600' : 'text-gray-400'} transition-colors duration-200`} />
                      </div>
                      <input
                        type="email"
                        name="email"
                        required
                        maxLength={100}
                        pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                        placeholder="Enter your email address"
                        value={formData.email}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField('')}
                        className={`block w-full pl-10 pr-4 py-4 border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${focusedField === 'email'
                          ? 'border-green-300 bg-green-50'
                          : 'border-gray-300 bg-white hover:border-gray-400'
                          }`}
                      />
                    </div>
                  </div>

                  {/* Message Field */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Message</label>
                    <div className="relative">
                      <div className="absolute top-4 left-3 pointer-events-none">
                        <MessageSquare className={`w-5 h-5 ${focusedField === 'message' ? 'text-green-600' : 'text-gray-400'} transition-colors duration-200`} />
                      </div>
                      <textarea
                        name="message"
                        required
                        maxLength={1000}
                        placeholder="Tell us how we can help you..."
                        rows={6}
                        value={formData.message}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('message')}
                        onBlur={() => setFocusedField('')}
                        className={`block w-full pl-10 pr-4 py-4 border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none ${focusedField === 'message'
                          ? 'border-green-300 bg-green-50'
                          : 'border-gray-300 bg-white hover:border-gray-400'
                          }`}
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl disabled:scale-100 disabled:shadow-none overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
                    <div className="flex items-center justify-center space-x-3 relative z-10">
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          <span>Send Message</span>
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                        </>
                      )}
                    </div>
                  </button>
                </form>
              )}

              {/* Features */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4 text-green-600" />
                    <span>Secure</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Leaf className="w-4 h-4 text-green-600" />
                    <span>Eco-Friendly</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Award className="w-4 h-4 text-green-600" />
                    <span>Trusted</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} space-y-8`}>
            {/* Contact Info Card */}
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8">
              <div className="mb-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-4 flex items-center">
                  <Globe className="mr-3 text-green-600" size={32} />
                  Contact Information
                </h3>
                <p className="text-gray-600 text-lg">Get in touch with us through any of these channels.</p>
              </div>

              <div className="space-y-6">
                {/* Location */}
                <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors duration-300">
                  <div className="p-3 bg-green-500 rounded-xl">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 text-lg mb-2">Our Location</h4>
                    <p className="text-gray-600">
                      1905 Sherman Street suite 200<br />
                      Denver Colorado 80203
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start space-x-4 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors duration-300">
                  <div className="p-3 bg-blue-500 rounded-xl">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 text-lg mb-2">Phone Number</h4>
                    <p className="text-gray-600 text-lg font-medium">+1(303) 872-0109</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4 p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors duration-300">
                  <div className="p-3 bg-purple-500 rounded-xl">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 text-lg mb-2">Email Address</h4>
                    <p className="text-gray-600 text-lg font-medium">greenecobagsusa@gmail.com</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8">
              <div className="mb-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-4 flex items-center">
                  <Clock className="mr-3 text-green-600" size={32} />
                  Business Hours
                </h3>
                <p className="text-gray-600 text-lg">We're here to help during these hours.</p>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                  <span className="text-gray-700 font-medium">Monday - Friday</span>
                  <span className="text-gray-900 font-semibold">8:00 AM - 9:00 PM</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                  <span className="text-gray-700 font-medium">Saturday</span>
                  <span className="text-gray-900 font-semibold">8:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-red-50 rounded-xl">
                  <span className="text-gray-700 font-medium">Sunday</span>
                  <span className="text-red-600 font-semibold">Closed</span>
                </div>
              </div>
            </div>

            {/* Quick Response */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 text-white">
              <div className="flex items-center space-x-3 mb-4">
                <Sparkles className="w-8 h-8" />
                <h3 className="text-2xl font-bold">Quick Response</h3>
              </div>
              <p className="text-green-100 text-lg mb-6">
                We typically respond to all inquiries within 24 hours during business days.
              </p>
              <div className="flex items-center space-x-2 text-green-100">
                <Heart className="w-5 h-5" />
                <span>We're here to help!</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default Contact;
